/** Size: max lots and max cost, saved on the strategy (docs/poi1.md §2.3,
 * docs/ema_trend.md).
 *
 * The engine sizes each trade itself — lots = min(max lots, floor(max cost /
 * (premium × lot size))) — and on the expiry day buys ITM-3 only if max lots of
 * it fit in max cost. These two numbers are the operator's control over that
 * (2026-09-14: "Max lots and max cost both"). The defaults, 15 and Rs 1,50,000,
 * are what the 2026 figures were measured on.
 *
 * POI-1 read them from 2026-09-14 and the 5/50 EMA trend from 2026-09-20, which
 * is why the card is no longer named for one engine.
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiError, strategiesApi } from "@/lib/api";
import type { Strategy } from "@/types/strategy";
import { STRATEGIES_KEY } from "./useStrategyActions";

const MIN_LOTS = 1;
const MAX_LOTS = 100;
const MIN_COST = 10_000;
const MAX_COST = 10_000_000;
const RULED_LOTS = 15;
const RULED_COST = 150_000;

function useSaveSize(strategyId: string, lots: string, cost: string, setNote: (s: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      strategiesApi.update(strategyId, {
        max_lots: Number(lots),
        max_cost: Number(cost),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      setNote("Saved — applies from the next entry.");
    },
    onError: (err) => {
      const reason = err instanceof ApiError ? `HTTP ${String(err.status)}` : "network";
      setNote(`Save failed: ${reason}`);
    },
  });
}

function SizeHelp({ manual, ruled }: Readonly<{ manual: boolean; ruled: boolean }>): ReactElement {
  return (
    <p className="mt-2 text-[11px] text-ink-muted">
      {manual
        ? "The chart sends the lots with each order; max lots is the cap of one entry: an order for more is not refused, it is taken at the cap (each broker has its own). Max cost only picks the strike of an index entry. "
        : "Each trade buys as many lots as fit in max cost, never more than max lots. "}
      Lots {String(MIN_LOTS)}–{String(MAX_LOTS)}, cost Rs {MIN_COST.toLocaleString("en-IN")}–
      {MAX_COST.toLocaleString("en-IN")}.
      {!manual && ruled && " These are the ruled values the 2026 results were measured on."}
      {!manual &&
        !ruled &&
        " ⚠️ Not the ruled 15 lots / Rs 1,50,000 — the 2026 results do not apply."}
    </p>
  );
}

/** What the confirmation says the cap will be. */
function capText(name: string, lots: string, cost: string): string {
  return `${name}: at most ${lots} lots in one entry (an order for more is taken at ${lots}), max cost Rs ${Number(cost).toLocaleString("en-IN")}.`;
}

function CapConfirm({
  open,
  text,
  busy,
  onCancel,
  onConfirm,
}: Readonly<{
  open: boolean;
  text: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}>): ReactElement {
  return (
    <ConfirmDialog
      open={open}
      title="Save the lots cap?"
      description={text}
      confirmLabel="Confirm and save"
      busy={busy}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

/** The server's cap (.env) is the most a trade may take: a Max lots above it is taken at the cap. */
function LotsCapWarning({
  cap,
  lots,
}: Readonly<{ cap: number | null; lots: string }>): ReactElement | null {
  const asked = Number(lots);
  if (cap === null || Number.isNaN(asked) || asked <= cap) return null;
  return (
    <p role="alert" className="mt-2 text-xs text-warning">
      ⚠️ The server allows at most {String(cap)} lots for this strategy, so trades will be taken at{" "}
      {String(cap)}, not {lots}. To allow {lots}, raise this strategy&apos;s lots cap in the
      server&apos;s .env file and restart.
    </p>
  );
}

function SizeTitle(): ReactElement {
  return (
    <div className="mb-2 flex items-center gap-2">
      <i className="ti ti-scale text-accent" />
      <span className="text-sm font-medium">Size</span>
    </div>
  );
}

export function StrategySizeCard({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement {
  const [lots, setLots] = useState(String(strategy.max_lots));
  const [cost, setCost] = useState(String(strategy.max_cost));
  const [note, setNote] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const mut = useSaveSize(strategy.id, lots, cost, (text) => {
    setNote(text);
    setAsking(false);
  });
  const valid = validLots(lots) && validCost(cost);
  const manual = strategy.strategy_type === "manual";
  const ruled = Number(lots) === RULED_LOTS && Number(cost) === RULED_COST;
  return (
    <section className="card mb-3">
      <SizeTitle />
      <div className="flex flex-wrap items-end gap-3">
        <NumberField label="Max lots" value={lots} onChange={setLots} />
        <NumberField label="Max cost (Rs)" value={cost} onChange={setCost} />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!valid || mut.isPending}
          onClick={() => {
            setNote(null);
            if (manual)
              setAsking(true); // a Manual trading cap is confirmed before it is saved
            else mut.mutate();
          }}
        >
          <i className="ti ti-device-floppy text-base" />
          Save size
        </button>
      </div>
      <SizeHelp manual={manual} ruled={ruled} />
      <LotsCapWarning cap={strategy.lots_cap ?? null} lots={lots} />
      {note !== null && <p className="mt-1 text-xs text-ink-secondary">{note}</p>}
      <CapConfirm
        open={asking}
        text={capText(strategy.name, lots, cost)}
        busy={mut.isPending}
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          mut.mutate();
        }}
      />
    </section>
  );
}

function validLots(v: string): boolean {
  const n = Number(v);
  return Number.isInteger(n) && n >= MIN_LOTS && n <= MAX_LOTS;
}

function validCost(v: string): boolean {
  const n = Number(v);
  return v.trim() !== "" && n >= MIN_COST && n <= MAX_COST;
}

function NumberField({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: string; onChange: (v: string) => void }>): ReactElement {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <input
        type="number"
        className="input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </label>
  );
}
